import { useEffect, useState } from "react";
import { FolderOpen } from "lucide-react";
import { db } from "@/lib/db";
import type { Collection, Document } from "@/types";

export default function Collections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [newName, setNewName] = useState("");
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [docs, setDocs] = useState<Document[]>([]);

  const loadCollections = () => {
    db.collections.toArray().then(setCollections);
  };

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (!selectedCollection) {
      setDocs([]);
      return;
    }
    db.collectionItems.where({ collectionId: selectedCollection }).toArray().then(async (items) => {
      const docs = await db.documents.bulkGet(items.map((i) => i.documentId));
      setDocs(docs.filter((d): d is Document => !!d));
    });
  }, [selectedCollection]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await db.collections.add({
      id: crypto.randomUUID(),
      name: newName.trim(),
      description: "",
      color: "#7C3AED",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setNewName("");
    loadCollections();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this collection?")) return;
    await db.collections.delete(id);
    await db.collectionItems.where({ collectionId: id }).delete();
    loadCollections();
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">New Collection</h3>
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collection name"
            className="flex-1 rounded-lg border border-border-strong bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
          />
          <button
            onClick={handleCreate}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            Create
          </button>
        </div>
      </div>

      {collections.length === 0 ? (
        <div className="rounded-xl border border-border-default bg-surface-0 p-8 text-center shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
            <FolderOpen size={24} className="text-text-muted" />
          </div>
          <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">No collections yet</p>
          <p className="mt-1 text-xs text-text-muted">Create a collection to organize your documents</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {collections.map((col) => (
            <div
              key={col.id}
              className={`cursor-pointer rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${
                selectedCollection === col.id
                  ? "border-accent bg-accent/5 dark:border-accent dark:bg-accent/10"
                  : "border-border-default bg-surface-0 dark:border-dark-border-default dark:bg-dark-surface-1"
              }`}
              onClick={() => setSelectedCollection(col.id === selectedCollection ? null : col.id)}
            >
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: col.color }} />
                <h4 className="font-medium text-text-primary dark:text-dark-text-primary">{col.name}</h4>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(col.id);
                }}
                className="mt-3 text-xs text-danger hover:underline"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedCollection && docs.length > 0 && (
        <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Documents in collection</h3>
          <div className="mt-3 space-y-2">
            {docs.map((doc) => (
              <div key={doc.id} className="text-sm text-text-secondary dark:text-dark-text-secondary">
                {doc.canonicalName}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
